import { RequirementsFileResponse } from '../../../../../../api/gms/group-category/group-category-api-state';
import { FileRequirement } from '../../../../../../features/gms-proposal-forms/attachment/file-requirement.state';
import { useIsSameUser } from '../../../../../../hooks/proposal/use-state-checker-proposal';

interface Props {
    isSaved: boolean;
    currentFile: File | null;
    uploadedFile: FileRequirement;
    fileR: RequirementsFileResponse;
    files: Record<number, File | null>;
    handleUploadClick: (value: number) => void;
    handleRemove: (value: number) => void;
    handleSave: (value: number) => void;
    handleView: (value: string) => void;
}

export default function RequirementItem({ isSaved, currentFile, uploadedFile, fileR, files, handleUploadClick, handleRemove, handleSave, handleView }: Props) {
    
    const isSameUser = useIsSameUser();
    const fileName = currentFile?.name || uploadedFile?.file_name;
    
    return (
        <div className="rounded-md border border-gray-200 bg-white transition hover:border-[#186cb2]/30 hover:shadow-sm">
            <div className="flex min-w-0 items-center gap-2.5 px-2.5 py-2">
                {/* Status Icon */}
                <div className="shrink-0">
                    {isSaved ? (
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-green-50">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" className="h-4 w-4 text-green-600" >
                                <path strokeLinecap="round" strokeLinejoin="round" d="m5 12 4 4L19 6" />
                            </svg>
                        </div>
                    ) : (
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4 text-gray-400" >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l2.5 2.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                            </svg>
                        </div>
                    )}
                </div>

                {/* Document Icon */}
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#186cb2]/10">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#186cb2" strokeWidth="1.7" className="h-4 w-4" >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v6h6M8 13h8M8 17h5" />
                    </svg>
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-gray-800" title={fileR.label}>
                        {fileR.label}
                    </p>

                    {fileName ? (
                        <div className="mt-0.5 flex min-w-0 items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3 w-3 shrink-0 text-gray-400" >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13 2v7h7" />
                            </svg>
                            <p className="truncate text-[10px] text-gray-500" title={fileName} >
                                {fileName}
                            </p>
                        </div>
                    ) : (
                        <p className="mt-0.5 text-[10px] text-gray-400">
                            No file uploaded
                        </p>
                    )}
                </div>

                {/* Actions */}
                <div className="flex shrink-0 items-center gap-1">
                    {isSameUser && (
                        <button
                            type="button"
                            onClick={() => handleUploadClick(fileR.id)}
                            title={uploadedFile?.is_uploaded ? 'Update file' : 'Upload file'}
                            className="flex h-7 w-7 items-center justify-center rounded-md text-[#186cb2] transition hover:bg-[#186cb2]/10"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0L7 9m5-5 5 5" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3" />
                            </svg>
                        </button>
                    )}

                    {currentFile && (
                        <>
                            <button
                                type="button"
                                onClick={() => handleRemove(fileR.id)}
                                title="Clear selected file"
                                className="flex h-7 w-7 items-center justify-center rounded-md text-red-500 transition hover:bg-red-50"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6 6 18" />
                                </svg>
                            </button>

                            <button
                                type="button"
                                onClick={() => handleSave(fileR.id)}
                                title="Save file"
                                className="flex h-7 w-7 items-center justify-center rounded-md text-green-600 transition hover:bg-green-50"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 4h11l3 3v13H5z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 4v5h8V4M8 20v-5h8v5" />
                                </svg>
                            </button>
                        </>
                    )}
                    {!currentFile && uploadedFile?.is_uploaded && (
                        <button
                            type="button"
                            onClick={() => handleView(uploadedFile.file_path)}
                            title="View file"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-green-600 transition hover:bg-green-50"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                                <circle cx="12" cy="12" r="2.5" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
